// Icon + tint for homepage tiles (Quick access cards, widgets, shortcut
// tiles). Tones match the side-nav drawer's section tints (src/SideNav.js),
// so a page reads as the same "area" everywhere it appears. Presentation
// only — names, paths and role gates still come from favoritesCatalog.
import AdminPanelSettingsRoundedIcon from "@mui/icons-material/AdminPanelSettingsRounded";
import AppsRoundedIcon from "@mui/icons-material/AppsRounded";
import AssignmentRoundedIcon from "@mui/icons-material/AssignmentRounded";
import CampaignRoundedIcon from "@mui/icons-material/CampaignRounded";
import DoorBackRoundedIcon from "@mui/icons-material/DoorBackRounded";
import DrawRoundedIcon from "@mui/icons-material/DrawRounded";
import EditNoteRoundedIcon from "@mui/icons-material/EditNoteRounded";
import AgricultureRoundedIcon from "@mui/icons-material/AgricultureRounded";
import FactCheckRoundedIcon from "@mui/icons-material/FactCheckRounded";
import ForumRoundedIcon from "@mui/icons-material/ForumRounded";
import GraphicEqRoundedIcon from "@mui/icons-material/GraphicEqRounded";
import RateReviewRoundedIcon from "@mui/icons-material/RateReviewRounded";
import FoundationRoundedIcon from "@mui/icons-material/FoundationRounded";
import HealthAndSafetyRoundedIcon from "@mui/icons-material/HealthAndSafetyRounded";
import HomeRepairServiceRoundedIcon from "@mui/icons-material/HomeRepairServiceRounded";
import InsightsRoundedIcon from "@mui/icons-material/InsightsRounded";
import KitchenRoundedIcon from "@mui/icons-material/KitchenRounded";
import LanguageRoundedIcon from "@mui/icons-material/LanguageRounded";
import LightbulbRoundedIcon from "@mui/icons-material/LightbulbRounded";
import KeyRoundedIcon from "@mui/icons-material/KeyRounded";
import ManageAccountsRoundedIcon from "@mui/icons-material/ManageAccountsRounded";
import LocalActivityRoundedIcon from "@mui/icons-material/LocalActivityRounded";
import LocalShippingRoundedIcon from "@mui/icons-material/LocalShippingRounded";
import PhoneIphoneRoundedIcon from "@mui/icons-material/PhoneIphoneRounded";
import PriceChangeRoundedIcon from "@mui/icons-material/PriceChangeRounded";
import PunchClockRoundedIcon from "@mui/icons-material/PunchClockRounded";
import ReportProblemRoundedIcon from "@mui/icons-material/ReportProblemRounded";
import ScheduleRoundedIcon from "@mui/icons-material/ScheduleRounded";
import SummarizeRoundedIcon from "@mui/icons-material/SummarizeRounded";
import TvRoundedIcon from "@mui/icons-material/TvRounded";
import WarehouseRoundedIcon from "@mui/icons-material/WarehouseRounded";
import WebRoundedIcon from "@mui/icons-material/WebRounded";
import WbSunnyRoundedIcon from "@mui/icons-material/WbSunnyRounded";
import { favoritesCatalog } from "./favoritesCatalog";
import { SAFETY_PAGES } from "../../views/Safety/safetyPages";

// Literal class strings so Tailwind keeps them.
export const TILE_TONES = {
  sky: "bg-sky-500/15 text-sky-600 dark:text-sky-300",
  indigo: "bg-indigo-500/15 text-indigo-600 dark:text-indigo-300",
  violet: "bg-violet-500/15 text-violet-600 dark:text-violet-300",
  emerald: "bg-emerald-500/15 text-emerald-600 dark:text-emerald-300",
  teal: "bg-teal-500/15 text-teal-600 dark:text-teal-300",
  amber: "bg-amber-500/15 text-amber-600 dark:text-amber-300",
  orange: "bg-orange-500/15 text-orange-600 dark:text-orange-300",
  rose: "bg-rose-500/15 text-rose-600 dark:text-rose-300",
  slate: "bg-slate-500/15 text-slate-600 dark:text-slate-300",
};

// Keys are the favorites-catalog group labels (see favoritesCatalog.js).
const GROUP_VISUALS = {
  Forms: { icon: EditNoteRoundedIcon, tone: "sky" },
  "Exterior Orders": { icon: WarehouseRoundedIcon, tone: "indigo" },
  "Window Quotes & Forms": { icon: DoorBackRoundedIcon, tone: "indigo" },
  "Lead Times": { icon: ScheduleRoundedIcon, tone: "indigo" },
  "Service Requests": { icon: HomeRepairServiceRoundedIcon, tone: "indigo" },
  "Sales & Leads": { icon: AssignmentRoundedIcon, tone: "emerald" },
  Cabinets: { icon: KitchenRoundedIcon, tone: "amber" },
  SBCF: { icon: FoundationRoundedIcon, tone: "orange" },
  Fleet: { icon: LocalShippingRoundedIcon, tone: "teal" },
  Safety: { icon: HealthAndSafetyRoundedIcon, tone: "rose" },
  "Everyday Tools": { icon: AppsRoundedIcon, tone: "violet" },
  Admin: { icon: AdminPanelSettingsRoundedIcon, tone: "slate" },
  "People & Access": { icon: ManageAccountsRoundedIcon, tone: "slate" },
  "Node Apps": { icon: PhoneIphoneRoundedIcon, tone: "violet" },
  Communications: { icon: CampaignRoundedIcon, tone: "teal" },
  "Shop Floor": { icon: PunchClockRoundedIcon, tone: "amber" },
};

// Pages whose own icon says more than their group's.
const PAGE_VISUALS = {
  "/forms/my-submissions": { icon: FactCheckRoundedIcon, tone: "sky" },
  "/webleads": { icon: LanguageRoundedIcon, tone: "emerald" },
  "/forms/recognition": { icon: LocalActivityRoundedIcon, tone: "emerald" },
  "/messages": { icon: ForumRoundedIcon, tone: "violet" },
  "/dashboard/weather": { icon: WbSunnyRoundedIcon, tone: "sky" },
  "/order-problems": { icon: ReportProblemRoundedIcon, tone: "rose" },
  "/forms/order-problem": { icon: ReportProblemRoundedIcon, tone: "rose" },
  "/forms/feature-request": { icon: LightbulbRoundedIcon, tone: "amber" },
  "/admin/feature-requests": { icon: LightbulbRoundedIcon, tone: "amber" },
  "/admin/user/userManager": { icon: ManageAccountsRoundedIcon, tone: "slate" },
  "/admin/access": { icon: KeyRoundedIcon, tone: "violet" },
  "/admin/app-codes": { icon: PhoneIphoneRoundedIcon, tone: "sky" },
  "/admin/assistant-usage": { icon: GraphicEqRoundedIcon, tone: "violet" },
  "/admin/feedback": { icon: RateReviewRoundedIcon, tone: "amber" },
  "/admin/signatures": { icon: DrawRoundedIcon, tone: "teal" },
  "/admin/webeditor": { icon: WebRoundedIcon, tone: "emerald" },
  "/windowdoor/summary": { icon: SummarizeRoundedIcon, tone: "indigo" },
  "/truthchart": { icon: InsightsRoundedIcon, tone: "emerald" },
  "/forms/IntDoorFormSummary": { icon: SummarizeRoundedIcon, tone: "emerald" },
  "/admin/doors/pricing": { icon: PriceChangeRoundedIcon, tone: "emerald" },
  "/fleet/forklifts": { icon: AgricultureRoundedIcon, tone: "teal" },
  "/fleet/inspections": { icon: FactCheckRoundedIcon, tone: "teal" },
  "/pcw": { icon: PunchClockRoundedIcon, tone: "slate" },
  "/mill-shop": { icon: PunchClockRoundedIcon, tone: "amber" },
  "/signage": { icon: TvRoundedIcon, tone: "slate" },
  ...Object.fromEntries(
    Object.values(SAFETY_PAGES).map((page) => [page.path, { icon: page.icon, tone: page.tone }])
  ),
};

const groupForPath = new Map();
favoritesCatalog.forEach((section) =>
  section.items.forEach((item) => {
    if (!groupForPath.has(item.path)) groupForPath.set(item.path, section.label);
  })
);

/** { icon, tone } for a page path; `group` overrides the catalog lookup. */
export const pageVisual = (path, group) =>
  PAGE_VISUALS[path] ||
  GROUP_VISUALS[group || groupForPath.get(path)] || {
    icon: AppsRoundedIcon,
    tone: "slate",
  };
